// Reader (Phase 2). By default loads THIS week's parasha online-first (calendar
// resolves it; rolls over Motzei Shabbos). Given a selection (from the text
// picker) it loads that parasha instead. Renders pasuk-by-pasuk (He hero + En,
// each verse keyed by its own ref, sanitized), wires each verse to the navigator
// (Phase 3/4 core) + the live "N sources explored" stat.

import { el, button } from "../lib/dom.ts";
import { setSafeHtml } from "../lib/sanitize.ts";
import { onIdle } from "../lib/idle.ts";
import { readerSkeleton } from "./skeleton.ts";
import { createFindBar } from "./findBar.ts";
import { buildInstallSection } from "./installPanel.ts";
import { currentInstallMode, runInstallPrompt } from "../lib/pwaInstall.ts";
import { createNavigator, verseShort, type NavHandle } from "./navigator.ts";
import { getParashaVerses, type ParashaText } from "../lib/sefaria.ts";
import { currentWeekClock } from "../lib/calendar.ts";
import { getSourceLangMode, type SourceDisplayMode } from "../lib/sourceLang.ts";
import { applyTheme, currentTheme, persistTheme, type Theme } from "../lib/theme.ts";
import {
  getTextScale,
  getTranslationShown,
  persistTextScale,
  persistTranslationShown,
  MIN_SCALE,
  MAX_SCALE,
} from "../lib/readerDisplay.ts";
import { humanizeError } from "../lib/errorCopy.ts";

export interface ReaderSelection {
  ref: string;
  en: string;
  he: string;
}

interface Header {
  he: string;
  en: string;
  ref: string;
}

// Deep-link hooks: `path` is an open-source chain to replay after render;
// `onPathChange` reports the open path back (a notification seam - the
// navigator itself owns the URL since v7); `onMounted` hands the caller the
// live NavHandle the moment a reader mounts - INCLUDING remounts from the
// error view's Retry button, which the promise return value cannot report.
export interface ReaderHooks {
  path?: string[];
  onPathChange?: (path: string[]) => void;
  onMounted?: (nav: NavHandle) => void;
}

// `stillCurrent` lets the caller cancel a superseded render: if the user navigates
// to another parasha while this one's network fetch is in flight, the stale
// response must not paint over the newer one (route-race-1). Defaults to always-on
// so direct callers and tests are unaffected.
// Returns the mounted NavHandle (v7: the app shell drives warm history
// restores through it), or null when the render failed / was superseded.
export async function renderReader(
  host: HTMLElement,
  selection?: ReaderSelection,
  stillCurrent: () => boolean = () => true,
  hooks: ReaderHooks = {},
): Promise<NavHandle | null> {
  host.replaceChildren(loadingEl(selection ? `Loading ${selection.en}...` : "Loading this week's parasha..."));
  let mounted: NavHandle | null = null;
  // mount RETURNS the nav (call sites assign `mounted`); it never reaches back
  // through the closure to set it, so the success path stays explicit.
  const mount = (header: Header, text: ParashaText): NavHandle => {
    const { wrap, nav } = buildReader(header, text, hooks.onPathChange, {
      // A deep link replays over the verse list (replayPath queries
      // .verse[data-ref] directly), so it opts out of chunked filling.
      syncAll: Boolean(hooks.path?.length),
      stillCurrent,
    });
    host.replaceChildren(wrap);
    if (hooks.path?.length) void nav.replayPath(hooks.path); // re-open the deep-linked place
    hooks.onMounted?.(nav);
    return nav;
  };
  try {
    if (selection) {
      const text = await getParashaVerses(selection.ref);
      if (!stillCurrent()) return null;
      mounted = mount(
        {
          he: selection.he,
          en: selection.en,
          ref: selection.ref,
        },
        text,
      );
    } else {
      const clock = await currentWeekClock();
      const text = await getParashaVerses(clock.week.ref);
      if (!stillCurrent()) return null;
      mounted = mount(
        {
          he: clock.week.parashaHe,
          en: clock.week.parasha,
          ref: clock.week.ref,
        },
        text,
      );
    }
  } catch (err) {
    if (!stillCurrent()) return null;
    // Manual retry renders unconditionally (the user explicitly asked for it).
    host.replaceChildren(errorEl(err, () => void renderReader(host, selection, () => true, hooks)));
  }
  return mounted;
}

// syncAll renders every verse in one pass (deep links + direct callers/tests);
// stillCurrent lets a superseded route stop the idle-time verse streaming.
interface BuildOpts {
  syncAll: boolean;
  stillCurrent: () => boolean;
}

function buildReader(
  header: Header,
  text: ParashaText,
  onPathChange?: (path: string[]) => void,
  opts: BuildOpts = { syncAll: true, stillCurrent: () => true },
): { wrap: HTMLElement; nav: ReturnType<typeof createNavigator> } {
  const wrap = el("div", "reader");

  const hdr = el("header", "phdr");
  const titles = el("div", "phdr-t");
  // The parasha name is the page's primary subject -> the one <h1>. The Hebrew
  // is the visual hero, so it carries the heading (UA margin reset in app.css so
  // the layout is unchanged); en/ref stay as supporting lines below it.
  const he = el("h1", "phdr-he");
  he.lang = "he";
  he.dir = "rtl";
  he.textContent = header.he ? `פרשת ${header.he}` : "";
  const en = el("div", "phdr-en", `Parashas ${header.en}`);
  // Label from the verses actually rendered, not the requested ref, so a short
  // final chapter could never overstate the range (phdr-ref-echo-1).
  const ref = el("div", "phdr-ref", versesRange(text.verses) || header.ref);
  // A quiet action prompt: the one thing a first-time reader needs to know -
  // the pasuk is tappable, and tapping it surfaces its sources in the Explore
  // panel (Open is a second tap). Stays visible (you can always tap another
  // verse), accent-tinted so it reads as an invitation. Wording is audited
  // against the real interaction model - do NOT claim a tap "opens" anything.
  const cta = el("div", "phdr-cta", "Tap any verse to see its sources, then Open any one to read it and follow the trail.");
  titles.append(he, en, ref, cta);
  hdr.append(titles);
  wrap.append(hdr);

  // navigator (expand-in-place sources) + sticky orientation bar (the GPS "top":
  // path strip + return routes) + Current Source card + the one aria-live position
  // region + the explored/kept stat. The orientation bar + live region live in the
  // shell's full-width top slot when present (desktop spans all columns); the
  // Current Source card sits inside the reader measure, directly above the text.
  // (Created BEFORE the controls so the "Show sources" control can route mode
  // changes through the navigator's applySourceLang.)
  const nav = createNavigator(header.en, header.ref, onPathChange);

  // small, calm reader controls (font size + translation visibility + the
  // source-list display mode) - Hebrew stays the hero.
  wrap.append(readerControls(wrap, nav));
  const slot = document.querySelector(".orient-slot");
  if (slot) {
    slot.replaceChildren(nav.orientationBar);
    // The aria-live region must NOT live inside .orient-slot: that landmark gets
    // inert + aria-hidden behind overlays (overlayBackground.ts), which silences a
    // live region nested in it. Mount it at shell level (never inerted) so toast /
    // position announcements still reach assistive tech while a drawer or modal is open.
    const liveHost = slot.closest(".shell") ?? document.body;
    liveHost.querySelector(":scope > .sr-status")?.remove();
    liveHost.append(nav.srStatus);
  } else {
    wrap.append(nav.orientationBar, nav.srStatus);
  }
  wrap.append(nav.currentSourceCard, nav.statline);

  const list = el("div", "verses");
  const addVerse = (v: ParashaText["verses"][number]): void => {
    const ve = verseEl(v.ref, v.he, v.en);
    nav.attachVerse(ve, v.ref);
    list.append(ve);
  };
  // Chunked fill (perceived perf, T5 - measured: 119 verses = a 184ms
  // main-thread block on desktop, ~4x that on phone-class CPUs): the first
  // screenful paints synchronously, the rest streams in idle-time batches.
  // Late verses are safe by construction - footprints/scent register per
  // verse via attachVerse, and find-in-parasha re-reads .verses per query.
  const FIRST = 12;
  const BATCH = 24;
  if (opts.syncAll || text.verses.length <= FIRST + BATCH) {
    text.verses.forEach(addVerse);
  } else {
    text.verses.slice(0, FIRST).forEach(addVerse);
    let i = FIRST;
    const idle = (cb: () => void): void => onIdle(cb, 0, 150);
    const fill = (): void => {
      if (!opts.stillCurrent()) return; // superseded route: stop streaming
      const end = Math.min(i + BATCH, text.verses.length);
      for (; i < end; i++) addVerse(text.verses[i]);
      if (i < text.verses.length) idle(fill);
    };
    idle(fill);
  }
  wrap.append(list);
  wrap.append(attribEl(text));
  return { wrap, nav };
}

let readerControlUid = 0;
const nextReaderControlId = (prefix: string): string => `${prefix}-${(readerControlUid += 1)}`;

// Calm reader controls: the tools dropdown contains font A-/A+ (scales the
// Hebrew + translation via a CSS var on the reader), a translation Show/Hide
// toggle, and the "Show sources" display mode. All three persist via Prefs
// (textScale / showTranslation / sourceLang), hydrated at boot so they open on the
// saved choice. Hebrew is always shown in the READER; it is the hero. The translation
// toggle controls the opened TEXT; "Show sources" controls the source LISTS - two
// different concepts, never merged.
function readerControls(reader: HTMLElement, nav: ReturnType<typeof createNavigator>): HTMLElement {
  const bar = el("div", "reader-controls");
  bar.setAttribute("role", "group");
  bar.setAttribute("aria-label", "Reader controls");

  const tools = el("div", "rc-tools");
  // Tools is an icon button in the header's .headeract family (sliders glyph) with a
  // hover tooltip; it opens the display-settings panel below.
  const toolsBtn = el("button", "headeract rc-tools-btn");
  toolsBtn.type = "button";
  toolsBtn.title = "Tools";
  toolsBtn.setAttribute("aria-label", "Tools - text size, appearance, translation, show sources");
  toolsBtn.setAttribute("aria-haspopup", "true");
  toolsBtn.setAttribute("aria-expanded", "false");
  const toolsIcon = el("span", "hi hi-tools");
  toolsIcon.setAttribute("aria-hidden", "true");
  toolsBtn.append(toolsIcon);

  const panel = el("div", "rc-tools-panel");
  const panelId = nextReaderControlId("reader-tools");
  panel.id = panelId;
  panel.hidden = true;
  panel.setAttribute("role", "group");
  panel.setAttribute("aria-label", "Reader display controls");
  toolsBtn.setAttribute("aria-controls", panelId);

  const setToolsOpen = (open: boolean): void => {
    panel.hidden = !open;
    toolsBtn.setAttribute("aria-expanded", String(open));
  };

  toolsBtn.addEventListener("click", () => setToolsOpen(panel.hidden));

  function detachToolsListeners(): void {
    document.removeEventListener("click", closeIfOutside, true);
    document.removeEventListener("keydown", closeOnEscape);
  }

  function closeIfOutside(e: MouseEvent): void {
    if (!tools.isConnected) {
      detachToolsListeners();
      return;
    }
    if (e.target instanceof Node && tools.contains(e.target)) return;
    setToolsOpen(false);
  }

  function closeOnEscape(e: KeyboardEvent): void {
    if (!tools.isConnected) {
      detachToolsListeners();
      return;
    }
    if (e.key !== "Escape" || panel.hidden) return;
    setToolsOpen(false);
    toolsBtn.focus();
  }

  document.addEventListener("click", closeIfOutside, true);
  document.addEventListener("keydown", closeOnEscape);

  // Settings rows: a de-emphasized label (left) + its control (right). The three
  // CHOICE controls share one segmented look (.rc-seg / .rc-seg-opt, keyed off
  // aria-pressed) so Appearance / Translation / Show sources read as one
  // language; Text size stays a stepper. (Upgrade per Refactoring UI: hierarchy +
  // consistency, no behavior change beyond adding Appearance.)
  const row = (labelText: string, control: HTMLElement): HTMLElement => {
    const r = el("div", "rc-row");
    r.append(el("span", "rc-label", labelText), control);
    return r;
  };
  const segGroup = (ariaLabel: string): HTMLElement => {
    const s = el("div", "rc-seg");
    s.setAttribute("role", "group");
    s.setAttribute("aria-label", ariaLabel);
    return s;
  };

  // Appearance (theme): moved out of the header - it does not need that
  // prominence. Reads the live data-theme each build, so a re-mounted reader
  // always reflects the current theme. aria-labels start with the visible text.
  const themeSeg = segGroup("Appearance");
  const paintTheme = (): void => {
    for (const b of themeSeg.querySelectorAll<HTMLButtonElement>(".rc-seg-opt")) {
      b.setAttribute("aria-pressed", String(b.dataset.theme === currentTheme()));
    }
  };
  for (const [val, label] of [["light", "Light"], ["dark", "Dark"]] as Array<[Theme, string]>) {
    const b = el("button", "rc-btn rc-seg-opt");
    b.type = "button";
    b.dataset.theme = val;
    b.textContent = label;
    b.setAttribute("aria-label", `${label} appearance`);
    b.addEventListener("click", () => {
      applyTheme(val, true); // explicit user toggle -> crossfade the colors
      void persistTheme(val);
      paintTheme();
    });
    themeSeg.append(b);
  }
  paintTheme();

  // Text size: live --reader-scale stepper, persisted in Prefs (seeded at boot).
  const MIN = MIN_SCALE;
  const MAX = MAX_SCALE;
  const STEP = 0.1;
  let scale = getTextScale();
  const sizeSeg = segGroup("Text size");
  const minus = el("button", "rc-btn rc-seg-opt");
  minus.type = "button";
  minus.textContent = "A-";
  minus.setAttribute("aria-label", "Smaller text");
  const plus = el("button", "rc-btn rc-seg-opt");
  plus.type = "button";
  plus.textContent = "A+";
  plus.setAttribute("aria-label", "Larger text");
  const applyScale = (): void => {
    // Set on :root so EVERY reading surface inherits it - including the Source Desk
    // dock and Peek sheets, which mount on document.body OUTSIDE .reader. Also kept
    // on .reader for the Explore panel's scale-copy + back-compat.
    document.documentElement.style.setProperty("--reader-scale", scale.toFixed(2));
    reader.style.setProperty("--reader-scale", scale.toFixed(2));
    minus.disabled = scale <= MIN + 1e-9;
    plus.disabled = scale >= MAX - 1e-9;
  };
  // The Explore panel reader sits outside .reader - a user change rebuilds it so it
  // re-reads the scale. Not done at mount: the navigator's own initial paint already
  // built the (source-less, scale-independent) panel, and any source opened later
  // reads the scale + hide-trans off .reader when it is built.
  minus.addEventListener("click", () => {
    scale = Math.max(MIN, Math.round((scale - STEP) * 100) / 100);
    applyScale();
    nav.refreshDisplay();
    void persistTextScale(scale);
  });
  plus.addEventListener("click", () => {
    scale = Math.min(MAX, Math.round((scale + STEP) * 100) / 100);
    applyScale();
    nav.refreshDisplay();
    void persistTextScale(scale);
  });
  sizeSeg.append(minus, plus);
  applyScale();

  // Translation: controls the opened TEXT's English (separate from "Show
  // sources" below). Segmented On/Off so it matches the other choice controls;
  // aria-pressed marks the active option (it agrees with the On/Off label).
  const transSeg = segGroup("Translation");
  let shown = getTranslationShown();
  const paintTrans = (): void => {
    reader.classList.toggle("hide-trans", !shown);
    for (const b of transSeg.querySelectorAll<HTMLButtonElement>(".rc-seg-opt")) {
      b.setAttribute("aria-pressed", String((b.dataset.on === "1") === shown));
    }
  };
  for (const [on, label] of [[true, "On"], [false, "Off"]] as Array<[boolean, string]>) {
    const b = el("button", "rc-btn rc-seg-opt");
    b.type = "button";
    b.dataset.on = on ? "1" : "0";
    b.textContent = label;
    b.setAttribute(
      "aria-label",
      on ? "Translation On - show the English translation" : "Translation Off - hide the English translation",
    );
    b.addEventListener("click", () => {
      shown = on;
      paintTrans();
      nav.refreshDisplay(); // the Explore panel reader sits outside .reader - rebuild so it honors hide-trans
      void persistTranslationShown(shown);
    });
    transSeg.append(b);
  }
  paintTrans();

  // "Show sources": which source/commentary cards the LISTS show, by readable
  // text availability (not just title language). English only genuinely means
  // "I can open this in English" - the filter keys off validated availability.
  const langSeg = segGroup("Show sources by language");
  // aria-labels START with the visible text (WCAG 2.5.3 Label in Name - voice
  // control users activate by what they see).
  const modes: Array<[SourceDisplayMode, string, string]> = [
    ["default", "Default", "Default - show every source, with Hebrew and English names"],
    ["english", "English only", "English only - show only sources available in English"],
    ["hebrew", "Hebrew only", "Hebrew only - show sources by their Hebrew names"],
  ];
  for (const [mode, label, aria] of modes) {
    const b = el("button", "rc-btn rc-lang rc-seg-opt");
    b.type = "button";
    b.dataset.mode = mode;
    b.textContent = label;
    b.setAttribute("aria-label", aria);
    b.setAttribute("aria-pressed", String(getSourceLangMode() === mode));
    // applySourceLang repaints every .rc-lang button (this one included),
    // persists the pref, and re-annotates all loaded lists.
    b.addEventListener("click", () => nav.applySourceLang(mode));
    langSeg.append(b);
  }

  panel.append(
    row("Appearance", themeSeg),
    row("Text size", sizeSeg),
    row("Translation", transSeg),
    row("Show sources", langSeg),
  );
  // "Install app": native prompt (Android / desktop Chromium) or a manual
  // Add-to-Home-Screen guide (iOS, or anywhere without a captured prompt). Null
  // when already installed. Resolved per mount, so a late prompt is picked up.
  const installSection = buildInstallSection({ mode: currentInstallMode(), onInstall: runInstallPrompt });
  if (installSection) panel.append(installSection);
  tools.append(toolsBtn, panel);

  // Tools lives in the top header (between the parasha picker and Bookmarks), filled
  // each mount like .orient-slot; the find bar stays in the reader-controls row.
  const headerToolsSlot = document.querySelector(".headertools");
  if (headerToolsSlot) headerToolsSlot.replaceChildren(tools);
  else bar.append(tools); // fallback (tests / no shell): keep it inline

  // "Find in this parasha": zero-network search over the loaded verses
  // (Hebrew nikud-insensitive + English).
  bar.append(createFindBar(() => reader.querySelector(".verses")));
  return bar;
}

function verseEl(ref: string, heHtml: string, enHtml: string): HTMLElement {
  const v = el("article", "verse");
  v.setAttribute("data-ref", ref);

  const label = el("div", "vref", verseShort(ref));
  const he = el("div", "vhe");
  he.lang = "he";
  he.dir = "rtl";
  setSafeHtml(he, heHtml);
  v.append(label, he);

  if (enHtml) {
    const en = el("p", "ven");
    en.lang = "en";
    setSafeHtml(en, enHtml);
    v.append(en);
  }
  return v;
}

// Quiet provenance (which Sefaria edition). Non-navigating: we never send the
// learner out to another site mid-learning - that is the whole point of the app.
function attribEl(text: ParashaText): HTMLElement {
  const a = el("footer", "attrib");
  const parts: string[] = [];
  if (text.heVersionTitle) parts.push(text.heVersionTitle);
  if (text.enVersionTitle) parts.push(text.enVersionTitle);
  a.textContent = parts.length ? `Text: ${parts.join(" / ")}, via Sefaria` : "Text via Sefaria";
  return a;
}

function loadingEl(msg: string): HTMLElement {
  return readerSkeleton(msg); // layout-true page skeleton, never a spinner in a void
}

function errorEl(err: unknown, retry: () => void): HTMLElement {
  const box = el("div", "errbox");
  box.setAttribute("role", "alert"); // announce the top-level failure to AT (matches errorRow)
  const friendly = humanizeError(err);
  const refresh = friendly.action === "refresh";
  // A spent session budget can't recover by Retry (the cap never resets in-session) -
  // offer a page Refresh, which restarts module state. Otherwise the usual Retry.
  box.append(el("div", "errbox-t", refresh ? "You've explored a lot this session." : "Sources are temporarily unavailable."));
  box.append(el("div", "errbox-d", refresh ? "Refresh the page to keep going." : friendly.message));
  box.append(refresh ? button("btn", "Refresh", () => location.reload()) : button("btn", "Retry", retry));
  return box;
}

// "Book c:v" (first verse) .. "c:v" (last verse) of what was actually rendered.
function versesRange(verses: ParashaText["verses"]): string {
  if (!verses.length) return "";
  const first = verses[0].ref;
  const lastVs = verses[verses.length - 1].ref.match(/(\d+:\d+)\s*$/);
  return lastVs ? `${first}-${lastVs[1]}` : first;
}
